// TaskActivityFeed.jsx - Minimal activity feed component
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Circle as StatusIcon,
  Person as AssignIcon,
  CalendarToday as DateIcon,
  Flag as PriorityIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Add as CreateIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// Import stores
import useActivityStore from '../../../stores/activityStore';

// Activity type icons
const ACTIVITY_ICONS = {
  created: CreateIcon,
  updated: EditIcon,
  status_changed: StatusIcon,
  assigned: AssignIcon,
  due_date_changed: DateIcon,
  priority_changed: PriorityIcon,
  comment_added: CommentIcon,
};

/**
 * TaskActivityFeed - Compact activity timeline for tasks
 * Shows task history with minimal space usage
 */
const TaskActivityFeed = ({ taskId }) => {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);
  
  // Activity store
  const {
    taskActivities,
    fetchTaskActivities,
    isLoading,
  } = useActivityStore();
  
  // Load activities
  useEffect(() => {
    if (taskId) {
      fetchTaskActivities(taskId);
    }
  }, [taskId, fetchTaskActivities]);
  
  // Get activities for this task
  const activities = taskActivities[taskId] || [];
  const visibleActivities = showAll ? activities : activities.slice(0, 5);
  const hiddenCount = activities.length - visibleActivities.length;
  
  // Format activity description
  const formatActivity = (activity) => {
    const { type, details, user } = activity;
    const userName = user ? `${user.firstName} ${user.lastName}` : 'Someone';
    
    switch (type) {
      case 'created':
        return `${userName} created this task`;
      
      case 'status_changed':
        return `${userName} changed status from ${details.from} to ${details.to}`;
      
      case 'assigned':
        return `${userName} assigned this to ${details.assignee?.firstName || 'someone'}`;
      
      case 'due_date_changed':
        return `${userName} ${details.dueDate ? 'set due date' : 'removed due date'}`;
      
      case 'priority_changed':
        return `${userName} changed priority from ${details.from} to ${details.to}`;
      
      case 'comment_added':
        return `${userName} added a comment`;
      
      default:
        return `${userName} updated the task`;
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="30%" height={14} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  
  // Empty state
  if (activities.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
      >
        No activity yet
      </Typography>
    );
  }
  
  return (
    <Box>
      {/* Activities list */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {visibleActivities.map((activity, index) => {
          const Icon = ACTIVITY_ICONS[activity.type] || EditIcon;
          const isLast = index === visibleActivities.length - 1;
          
          return (
            <Box
              key={activity._id || index}
              sx={{
                display: 'flex',
                gap: 1,
                position: 'relative',
                pb: isLast ? 0 : 2.5,
              }}
            >
              {/* Timeline line */}
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 14,
                    top: 28,
                    bottom: 0,
                    width: 2,
                    bgcolor: alpha(theme.palette.divider, 0.2),
                    borderRadius: 1,
                  }}
                />
              )}
              
              {/* Icon */}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  zIndex: 1,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                  }
                }}
              >
                <Icon sx={{ fontSize: 16, color: 'primary.main', zIndex: 1 }} />
              </Box>
              
              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 1, 
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    borderColor: alpha(theme.palette.divider, 0.12),
                  }
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      lineHeight: 1.4,
                      mb: 0.5,
                      fontWeight: 500,
                    }}
                  >
                    {formatActivity(activity)}
                  </Typography>
                  
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: 11,
                      opacity: 0.8,
                    }}
                  >
                    {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      
      {/* Show more button */}
      {hiddenCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowAll(!showAll)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontSize: 12,
              px: 2,
              py: 0.5,
              borderColor: alpha(theme.palette.divider, 0.2),
              color: 'text.secondary',
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.3),
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }
            }}
          >
            {showAll ? 'Show less' : `Show ${hiddenCount} more`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TaskActivityFeed;